import depsloader from "../../../depsloader";

const SEARCH_URL = "https://api.screenwork.de/geocoder/search";
const REVERSE_URL = "https://api.screenwork.de/geocoder/reverse";
const REQUEST_TIMEOUT_MS = 20000;

// Der Maps-Key wird serverseitig je Request ermittelt und am <html>-Element
// ausgegeben (data-maps-key); er ist nie im Bundle eingebaut.
function getMapsKey() {
  if (typeof document === "undefined" || !document.documentElement) {
    return "";
  }

  const key = document.documentElement.getAttribute("data-maps-key");
  return typeof key === "string" ? key.trim() : "";
}

function cleanString(value) {
  if (typeof value === "number" && isFinite(value)) {
    return String(value);
  }

  if (typeof value === "string" && value.trim() !== "") {
    return value.trim();
  }

  return null;
}

function validCoordinates(lat, lng) {
  return typeof lat === "number" && typeof lng === "number" &&
    isFinite(lat) && isFinite(lng) &&
    lat >= -90 && lat <= 90 && lng >= -180 && lng <= 180;
}

const LEGACY_DISTRICT_PLACES = ["suburb", "quarter", "neighbourhood", "borough"];
const LEGACY_CITY_PLACES = ["city", "town", "village", "hamlet"];

class Geocoder {
  constructor(axios) {
    this.axios = axios;
    this.pending = null;
    this.sequence = 0;
  }

  // Beendet eine laufende Suche; spätere Antworten werden verworfen.
  cancelAutocomplete() {
    this.sequence++;

    if (this.pending != null) {
      this.pending.cancel();
      this.pending = null;
    }
  }

  _geoaddressText(g) {
    let s = "";
    if (g.zip && g.city && g.district) {
      s = g.zip + " " + g.city + "–" + g.district;
    } else if (g.zip && g.city) {
      s = g.zip + " " + g.city;
    } else if (g.city && g.district) {
      s = g.city + "–" + g.district;
    } else if (g.city) {
      s = g.city;
    } else if (g.zip && g.district) {
      s = g.zip + " " + g.district;
    } else if (g.district) {
      s = g.district;
    }

    const p = [];

    if (g.street && g.number) {
      p.push(g.street + " " + g.number);
    } else if (g.street) {
      p.push(g.street);
    }

    if (s) {
      p.push(s);
    }

    if (g.state && !g.street) {
      p.push(g.state);
    }

    if (g.country && !g.street) {
      p.push(g.country);
    }

    return p.join(", ");
  }

  // Wandelt ein Feature in das bisherige Ergebnisformat um, ohne es zu verändern.
  _geoaddress(feature) {
    if (!feature || typeof feature !== "object") {
      return null;
    }

    const properties = feature.properties && typeof feature.properties === "object" ? feature.properties : {};
    const coordinates = feature.geometry && Array.isArray(feature.geometry.coordinates) ? feature.geometry.coordinates : [];

    // GeoJSON liefert [lng, lat].
    const lng = typeof coordinates[0] === "number" ? coordinates[0] : NaN;
    const lat = typeof coordinates[1] === "number" ? coordinates[1] : NaN;
    if (!validCoordinates(lat, lng)) {
      return null;
    }

    const name = cleanString(properties.name);
    const type = cleanString(properties.type);
    let street = cleanString(properties.street);
    const number = cleanString(properties.housenumber);
    const zip = cleanString(properties.postcode);
    let city = cleanString(properties.city);
    let district = cleanString(properties.district);

    if (type) {
      switch (type) {
        case "house":
          break;
        case "street":
          if (!street) {
            street = name;
          }
          break;
        case "city":
          if (!city) {
            city = name;
          }
          break;
        case "district":
        case "locality":
          if (!district && name && name !== city) {
            district = name;
          }
          break;
        default:
          break;
      }
    } else {
      // Legacy-Fallback für Antworten ohne properties.type.
      const osmKey = cleanString(properties.osm_key);
      const osmValue = cleanString(properties.osm_value);

      if (osmKey === "highway" && !street) {
        street = name;
      } else if (osmKey === "boundary" || (osmKey === "place" && LEGACY_DISTRICT_PLACES.indexOf(osmValue) !== -1)) {
        if (!district && name && name !== city) {
          district = name;
        }
      } else if (osmKey === "place" && LEGACY_CITY_PLACES.indexOf(osmValue) !== -1 && !city) {
        city = name;
      }
    }

    if (district && district === city) {
      district = null;
    }

    const geoaddress = {
      street: street || undefined,
      number: street && number ? number : undefined,
      zip: zip || undefined,
      city: city || undefined,
      district: district || undefined,
      lat,
      lng,
      state: cleanString(properties.state) || undefined,
      country: cleanString(properties.country) || undefined
    };

    let text = this._geoaddressText(geoaddress);
    if (!street && !city && !district && name) {
      // Ohne Orts- oder Straßenangabe bleibt der Name die Beschriftung.
      text = text ? name + ", " + text : name;
    }

    if (!text) {
      return null;
    }

    geoaddress.text = text;
    return geoaddress;
  }

  _features(data) {
    if (!data || typeof data !== "object" || !Array.isArray(data.features)) {
      return [];
    }

    return data.features;
  }

  autocomplete(q) {
    this.cancelAutocomplete();

    const query = typeof q === "string" ? q.trim() : "";
    const key = getMapsKey();
    if (!query || !key) {
      return Promise.resolve([]);
    }

    const sequence = this.sequence;
    const source = this.axios.CancelToken.source();
    this.pending = source;

    const params = new URLSearchParams();
    params.append("q", query);
    params.append("lang", "de");
    params.append("limit", "10");
    params.append("key", key);

    const done = () => {
      if (this.pending === source) {
        this.pending = null;
      }
    };

    // Ergebnis: Array mit Treffern, null für abgebrochene oder überholte Suchen.
    return this.axios.get(SEARCH_URL, {
      cancelToken: source.token,
      timeout: REQUEST_TIMEOUT_MS,
      params
    }).then(res => {
      done();
      if (sequence !== this.sequence) {
        return null;
      }

      const results = [];
      this._features(res && res.data).forEach(feature => {
        const geoaddress = this._geoaddress(feature);
        if (geoaddress) {
          results.push(geoaddress);
        }
      });

      return results;
    }, thrown => {
      done();
      if (this.axios.isCancel(thrown) || sequence !== this.sequence) {
        return null;
      }

      throw thrown;
    });
  }

  reverse(lat, lng) {
    const latitude = typeof lat === "string" ? parseFloat(lat) : lat;
    const longitude = typeof lng === "string" ? parseFloat(lng) : lng;

    if (!validCoordinates(latitude, longitude)) {
      return Promise.reject(new Error("invalid coordinates"));
    }

    const key = getMapsKey();
    if (!key) {
      return Promise.reject(new Error("maps key missing"));
    }

    const params = new URLSearchParams();
    params.append("lat", String(latitude));
    params.append("lon", String(longitude));
    params.append("lang", "de");
    params.append("key", key);

    return this.axios.get(REVERSE_URL, {
      timeout: REQUEST_TIMEOUT_MS,
      params
    }).then(res => {
      const features = this._features(res && res.data);
      for (let i = 0; i < features.length; i++) {
        const geoaddress = this._geoaddress(features[i]);
        if (geoaddress) {
          return geoaddress;
        }
      }

      throw new Error("invalid data");
    });
  }
}

function GeocoderFactory() {
  return new Promise(resolve => {
    depsloader.load("axios").then(axios => {
      resolve(new Geocoder(axios));
    })
  });
}

export { GeocoderFactory };

export default {};
